import { ChangeDetectionStrategy, Component, computed, input, model, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroInformationCircleSolid } from '@ng-icons/heroicons/solid';
import { SelectOptionGroup } from '@app/_api/model';

@Component({
  selector: 'if-multiselect',
  templateUrl: './multi-select.component.html',
  imports: [NgTemplateOutlet, NgIcon],
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [provideIcons({ heroInformationCircleSolid })],
})
export class MultiSelectComponent<T> {
  readonly selectedOptions = model<T[]>([]);
  readonly preNote = input<TemplateRef<HTMLElement>>();
  readonly footnote = input<TemplateRef<HTMLElement>>();
  readonly selectionLabel = input<string>();
  readonly optionGroups = input.required<SelectOptionGroup<T>[]>();
  readonly disabledPreselectedOptions = input([] as T[]);
  readonly selection = input<string>();
  readonly showSelectAll = input(true);
  readonly showDeleteAll = input(false);

  private readonly $allOptions = computed(() =>
    this.optionGroups()
      .flatMap(optionGroup => optionGroup.models)
      .map(option => option.object),
  );

  isAllSelected() {
    return this.selectedOptions().length === this.$allOptions().length;
  }

  isSelected(option: T) {
    return this.selectedOptions().some(selectedOption => JSON.stringify(selectedOption) === JSON.stringify(option));
  }

  onCheckedChange(checkedEvent: Event, option: T) {
    const checked = (checkedEvent.target as HTMLInputElement).checked;

    if (checked) this.selectedOptions.update(selectedOptions => [...selectedOptions, option]);
    else
      this.selectedOptions.update(selectedOptions =>
        selectedOptions.filter(selectedOption => JSON.stringify(selectedOption) !== JSON.stringify(option)),
      );
  }

  onSelectAll() {
    if (this.isAllSelected()) this.unselectAll();
    else this.selectAll();
  }

  unselectAll() {
    this.selectedOptions.set([...this.disabledPreselectedOptions()]);
  }

  private selectAll() {
    this.selectedOptions.set([...this.$allOptions()]);
  }
}
